import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import { useStoreActions, useStoreState } from "easy-peasy";
import { isMobile } from "../../utils/ResponsiveUtils";
import { enterFullScreen } from "../../utils/screen";
import analytics from "../../utils/analytics";

const IntroScreen = ({ content, webResponsiveUtil }) => {
  const CONTAINER_ID = "interactiveExperienceIntro";
  const bgRef = useRef(null);
  const titleRef = useRef(null);
  const byLineRef = useRef(null);
  const subtitleRef = useRef(null);
  const btnRef = useRef(null);
  const containerRef = useRef(null);

  const { title, byline, subtitle, introBtn, background } = content;
  const { setPlaying: setVideoPlaying } = useStoreActions(
    (actions) => actions.stage
  );
  const { setVisible: setDemoVisible } = useStoreActions(
    (actions) => actions.demo
  );
  const titleInitialState = {
    opacity: 0,
    scale: 0,
  };
  const isIntroVisible = useStoreState((state) => state.intro.isVisible);
  const audioPlayer = useStoreState((state) => state.bgm.player);
  const videoPlayer = useStoreState((state) => state.stage.player);
  const { isPlaying: isExperiencePlaying, isFirstPlay } = useStoreState(
    (state) => state.experience
  );
  const setIntroVisible = useStoreActions(
    (actions) => actions.intro.setVisible
  );

  const { setPlaying: setExperiencePlaying, setFirstPlay } = useStoreActions(
    (actions) => actions.experience
  );
  const playCount = useStoreState((state) => state.analytics.playCount);
  const setPlayCount = useStoreActions(
    (actions) => actions.analytics.setPlayCount
  );

  const [bgImageLoaded, setBgImageLoaded] = useState(false);
  const [bgImageError, setBgImageError] = useState(false);

  const showIntro = () => {
    if (containerRef && containerRef.current) {
      containerRef.current.classList.remove("is-fade-out");
    }
  };

  const hideIntro = () => {
    if (containerRef && containerRef.current) {
      containerRef.current.classList.add("is-fade-out");
    }
  };

  const handleStartClick = (e) => {
    e.currentTarget.classList.add("is-animated--outro");
    analytics.trackStartCardCTA();
    if (audioPlayer) {
      audioPlayer.play();
    }
    setTimeout(() => {
      if (isMobile()) {
        enterFullScreen();
      }
      if (videoPlayer) {
        const playPromise = videoPlayer.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            setVideoPlaying(true);
            // Increase play count
            setPlayCount(playCount + 1);
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.log('Play was cancelled', error);
          });
        }
      }
      btnRef.current.classList.remove("is-animated--outro");
    }, 950);

    // First play
    if (!isExperiencePlaying) {
      setExperiencePlaying(true);
    }
    if (isFirstPlay) {
      setFirstPlay(false);
    }
  };

  const handleContainerTransitionEnd = (e) => {
    if (e.currentTarget.classList.contains("is-fade-out")) {
      setDemoVisible(true);
    }
  };

  const handleAnimationEnd = (e) => {
    if (!e.currentTarget.classList.contains("is-outro-ready"))
      e.currentTarget.classList.add("is-outro-ready");
    if (e.currentTarget.classList.contains("is-animated--outro")) {
      setIntroVisible(false);
    }
  };

  const startAnimation = useCallback(() => {
    if (titleRef && titleRef.current) {
      titleRef.current.classList.add("is-animated");
    }
    if (btnRef && btnRef.current) {
      btnRef.current.classList.add("is-animated");
    }
    if (byLineRef && byLineRef.current) {
      byLineRef.current.classList.add("is-animated");
    }
    if (subtitleRef && subtitleRef.current) {
      subtitleRef.current.classList.add("is-animated");
    }
  }, [titleRef, btnRef, subtitleRef, byLineRef]);

  useEffect(() => {
    if (!isEmpty(content)) {
      if (background && bgImageLoaded) {
        if (bgRef && bgRef.current) {
          bgRef.current.classList.add("is-animated");
        }
        startAnimation();
      } else if (!background || (background && bgImageError)) {
        startAnimation();
      }
    }
    return () => {};
  }, [content, bgImageLoaded, bgImageError, background, startAnimation]);

  useEffect(() => {
    if (isIntroVisible) {
      showIntro();
    } else {
      hideIntro();
    }
    return () => {};
  }, [isIntroVisible]);

  return (
    !isEmpty(content) && (
      <div
        id={CONTAINER_ID}
        ref={containerRef}
        onTransitionEnd={handleContainerTransitionEnd}
      >
        <div className="experience-intro">
          <div ref={bgRef} className="experience-intro__bg-wrapper ">
            <div className="experience-intro__bg">
              <img
                src={background.backgroundUrl}
                className="visually-hidden"
                alt={background.backgroundAltText}
                onLoad={() => {
                  setBgImageLoaded(true);
                }}
                onError={() => {
                  setBgImageError(true);
                }}
              />
            </div>
          </div>
          <div className="experience-intro__inner">
            <div className="experience-intro__content">
              {title.show && (
                <h2 ref={titleRef} className="experience-intro__title">
                  <span
                    className="experience-intro__title-inner"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: title.text,
                    }}
                  />
                </h2>
              )}
              {byline.show && (
                <p
                  ref={byLineRef}
                  className="experience-intro__byline"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: byline.text,
                  }}
                />
              )}
              {subtitle.show && (
                <h3
                  ref={subtitleRef}
                  className="experience-intro__subtitle"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: subtitle.text,
                  }}
                />
              )}
            </div>
            <button
              ref={btnRef}
              className="js-intro__btn experience-intro__btn"
              type="button"
              onClick={handleStartClick}
              onTransitionEnd={handleAnimationEnd}
            >
              <span className="experience-intro__btn__inner">
                {introBtn.iconUrl && (
                  <span className="experience-intro__btn-icon">
                    <img src={introBtn.iconUrl} alt={introBtn.iconAltText} />
                  </span>
                )}
                <span className="experience-intro__btn-label">
                  {introBtn.text}
                </span>
              </span>
            </button>
          </div>
        </div>
        <style jsx="true">
          {`
              #${CONTAINER_ID}{
                transition: opacity 0.5s ease-in !important;
                height: 100% !important;
                width: 100% !important;
                position: absolute !important;
              }  
              #${CONTAINER_ID}.is-fade-out {
                opacity: 0;
              }
              #${CONTAINER_ID} .experience-intro {
                position: absolute !important;
                z-index: 2 !important;
                top: 0 !important;
                left: 0 !important;

                display: flex !important;
                overflow: hidden !important;

                width: 100% !important;
                height: 100% !important;

                background: #000000 !important;

                align-items: center !important;
                justify-content: center !important;
              }

              #${CONTAINER_ID} .experience-intro.is-hidden {
                z-index: -10 !important;
              }

              #${CONTAINER_ID} .experience-intro__inner {
                position: relative !important;
              }

              #${CONTAINER_ID} .experience-intro__title {
                transform: scale(${titleInitialState.scale}) !important;
                opacity: ${titleInitialState.opacity} !important;
                margin: 0 !important;
                padding: 0 !important;
                text-align: center !important;
                font-weight: normal !important;
                color: ${title.color} !important;
                line-height: 1 !important;
                transition: transform 1s ease-out, opacity 1s ease-out !important;
              }

              #${CONTAINER_ID} .experience-intro__title.is-animated {
                transform: scale(1) !important;
                opacity: 1 !important;
              }

              #${CONTAINER_ID} .experience-intro__title-inner {
                display: inline-block !important;
                font-size: ${webResponsiveUtil.getFontSize(80)}px !important;
                font-family: "${title.font}", sans-serif !important;
              }
              #${CONTAINER_ID} .experience-intro__title-inner strong {
                font-weight: 600 !important;
              }

              #${CONTAINER_ID} .experience-intro__subtitle {
                transform: scale(0) !important;
                font-size: ${webResponsiveUtil.getFontSize(45)}px !important;
                font-family: ${subtitle.font} !important;
                margin: 0 !important;
                padding: 0 !important;
                -webkit-animation-delay: 0.85s !important;
                animation-delay: 0.85s !important;
                text-align: center !important;

                color: ${subtitle.color} !important;
                line-height: 1 !important;
                transition: transform 0.8s ease-out !important;
                font-weight: normal !important;
              }
              #${CONTAINER_ID} .experience-intro__subtitle strong {
                font-weight: 600 !important;
              }
              #${CONTAINER_ID} .experience-intro__subtitle.is-animated {
                transform: scale(1) !important;
                transition-delay: 1s !important;
              }

              #${CONTAINER_ID} .experience-intro__byline {
                transform: scale(0) !important;
                font-size: ${webResponsiveUtil.getFontSize(24)}px !important;

                margin: ${webResponsiveUtil.getHeightValue(
                  30
                )}px 0 ${webResponsiveUtil.getHeightValue(35)}px !important;

                -webkit-animation-delay: 0.5s !important;
                animation-delay: 0.5s !important;
                text-align: center !important;
                letter-spacing: ${webResponsiveUtil.getWidthValue(
                  11
                )}px !important;
                text-transform: uppercase !important;

                color: ${byline.color} !important;
                font-family: ${byline.font} !important;
                transition: transform 1s ease-out !important;
              }
              #${CONTAINER_ID} .experience-intro__byline.is-animated {
                transform: scale(1) !important;
                transition-delay: 0.75s !important;
                transition-duration: 0.5s !important;
              }

              #${CONTAINER_ID} .experience-intro__btn {
                font-size: ${webResponsiveUtil.getFontSize(68)}px !important;
                font-family: ${introBtn.font} !important;
                display: flex !important;
                margin-top: ${webResponsiveUtil.getHeightValue(
                  80
                )}px !important;
                padding: ${webResponsiveUtil.getHeightValue(28)}px 
                          ${webResponsiveUtil.getWidthValue(0)}px 
                          ${webResponsiveUtil.getHeightValue(26)}px !important;

                text-align: center !important;
                letter-spacing: 0.7px !important;

                color: ${introBtn.color} !important;
                border: 0 !important;
                border-radius: ${webResponsiveUtil.getWidthValue(
                  35
                )}px !important;
                background: ${introBtn.bgColor} !important;
                box-shadow: none !important;
                align-items: center !important;
                justify-content: flex-start !important;
                transform: scale3d(0, 1, 1) !important;
                transform-origin: 0 0 !important;
                overflow: hidden !important;
                transition: opacity 1s ease-out, transform 1s ease-out !important;
              }
              #${CONTAINER_ID} .experience-intro__btn:hover,
              #${CONTAINER_ID} .experience-intro__btn *:hover{
                cursor: pointer !important;
              }
              #${CONTAINER_ID} .experience-intro__btn.is-animated {
                transform: scale3d(1,1,1) !important;
              }
              #${CONTAINER_ID} .experience-intro__btn.is-outro-ready {
                transform-origin: 50% 50% !important;
              }
              #${CONTAINER_ID} .experience-intro__btn.is-animated--outro {
                transform: scale3d(2,2,1) !important;
                opacity: 0 !important;
                transition-duration: 0.5s !important;
                transition-timing-function: ease-in !important;
                
              }
              #${CONTAINER_ID} .experience-intro__btn__inner {
                display: flex !important;
                padding: ${webResponsiveUtil.getHeightValue(0)}px 
                          ${webResponsiveUtil.getWidthValue(84)}px !important;
                justify-content: center !imporntant;
              }
              
              #${CONTAINER_ID} .experience-intro__btn:focus,
              #${CONTAINER_ID} .experience-intro__btn:active {
                outline: 0 !important;
              }

              #${CONTAINER_ID} .experience-intro__bg-wrapper {
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                opacity: 0.01 !important;
                width: 100% !important;
                height: 100% !important;
                transition: opacity 1.5s ease-out !important;
              }
              #${CONTAINER_ID} .experience-intro__bg-wrapper.is-animated {
                opacity: 1 !important;
              }

              #${CONTAINER_ID} .experience-intro__bg {
                width: 100% !important;
                height: 100% !important;
                opacity: 0.5 !important;
                background: url("${
                  background.backgroundUrl
                }") no-repeat 50% 50% !important;
                background-size: cover !important;
              }

              #${CONTAINER_ID} .experience-intro__btn-icon {
                display: inline-block !important;

                min-width: ${webResponsiveUtil.getWidthValue(90)}px !important;
                width: ${webResponsiveUtil.getWidthValue(90)}px !important;
                margin-right: ${webResponsiveUtil.getWidthValue(
                  33
                )}px !important;
              }

              #${CONTAINER_ID} .experience-intro__btn-icon img {
                display: block !important;
                width: 100% !important;
              }

              #${CONTAINER_ID} .experience-intro__btn-label {
                font-weight: 600 !important;
                white-space: nowrap !important;
              }
            `}
        </style>
      </div>
    )
  );
};

IntroScreen.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.shape({
      text: PropTypes.string,
      font: PropTypes.string,
      color: PropTypes.string,
      show: PropTypes.bool,
    }),
    subtitle: PropTypes.shape({
      text: PropTypes.string,
      font: PropTypes.string,
      color: PropTypes.string,
      show: PropTypes.bool,
    }),
    byline: PropTypes.shape({
      text: PropTypes.string,
      font: PropTypes.string,
      color: PropTypes.string,
      show: PropTypes.bool,
    }),
    introBtn: PropTypes.shape({
      text: PropTypes.string,
      font: PropTypes.string,
      color: PropTypes.string,
      bgColor: PropTypes.string,
      iconUrl: PropTypes.string,
      iconAltText: PropTypes.string,
    }),
    background: PropTypes.shape({
      backgroundUrl: PropTypes.string,
      backgroundAltText: PropTypes.string,
    }),
  }),
  webResponsiveUtil: PropTypes.shape({
    getHeightValue: PropTypes.func,
    getWidthValue: PropTypes.func,
    getFontSize: PropTypes.func,
  }),
  product: PropTypes.shape({
    name: PropTypes.string,
  }),
};

IntroScreen.defaultProps = {
  content: {},
  webResponsiveUtil: {
    getHeightValue: () => {},
    getWidthValue: () => {},
    getFontSize: () => {},
  },
  product: {
    name: "",
  },
};

export default IntroScreen;
